import { Action, Location } from 'history';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Admin2FAModal,
    Alert,
    autoinject,
    bind, Contract,
    ErrorStore,
    ErrorView,
    ImpersonatingAlert,
    inject,
    LoadGoogleMapScript,
    MainContainer, ModalHandle,
    NotificationAlert,
    NotificationToastList,
    PrivateRoute,
    SidebarObfuscator, toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsView } from '../Accounts/AccountsView';
import { AdminView } from '../Admin/AdminView';
import { DisplayAlertsView } from '../Alerts/DisplayAlertsView';
import { AuditStore } from '../Audit/AuditStore';
import { BIView } from '../BI/BIView';
import { Dashboard } from '../Dashboard/Dashboard';
import { DisplayList } from '../Displays/DisplayList';
import { DisplayView } from '../Displays/DisplayView';
import { FavoritesList } from '../Displays/FavoritesList';
import { DocumentsView } from '../Documents/DocumentsView';
import { InvitesList } from '../Invites/InvitesList';
import { Preferences } from '../Manage/Preferences';
import { TicketsView } from '../Tickets/TicketsView';
import { UsersView } from '../Users/UsersView';
import { WarrantyAlertsView } from '../WarrantyAlerts/WarrantyAlertsView';
import { AppStore } from './AppStore';
import { ContactUsView } from './ContactUsView';
import { Header } from './Header';
import { NotFound } from './NotFound';
import { Sidebar } from './Sidebar';
import {ContactExpirationAlertsModal} from "../Contracts/ContractExpirationAlertsModal";
import {ContractsStore} from "../Contracts/ContractsStore";

const googleMapApiKey = (window as any).initialGoogleMapApiKey as string;

export interface IAppProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class App extends React.Component<IAppProps> {
    @action
    public async componentDidMount() {
        try {
            this.loading = true;
            await this.accountStore.loadUserAsync();
            const user = this.accountStore.user;

            if (user) {
                this.appStore.storage.theme = user.preferredTheme as any;
            }
            
            await this.contractsStore.loadAllAsync();
        } catch (e: any) {
            console.error(e);
        } finally {
            this.loading = false;
        }

        if (
            !this.accountStore.user?.twoFactorEnabled &&
            !this.accountStore.user?.isImpersonating &&
            !this.accountStore.isTestUser &&
            this.props.location.pathname != '/preferences'
        ) {
            const message: React.ReactNode = (
                <div>
                    2 Factor Authentication is not enabled on your account.
                    <LinkContainer to="/preferences" className="text-white">
                        <a
                            className="btn btn-sm btn-outline-white text-white mx-3"
                            onClick={this.appStore.notificationHandle.close}
                        >
                            Secure Account
                        </a>
                    </LinkContainer>
                </div>
            );

            this.appStore.notificationHandle.set('teal', message, undefined, false);
        }

        await this.reportPageView(this.props.location);
        this.props.history.listen(this.onHistoryChange);
        
        this.initializeAccountStorage();
        
        if(this.shouldOpenContractsExpirationModal) {
            this.contractAlertModalHandle.open();
        }
    }

    public render() {
        if (this.loading) return <div>Loading...</div>;

        return (
            <ErrorView error={this.appStore.error} errorStore={this.errorStore}>
                <div className={this.layoutClassName}>
                    <Header history={this.props.history} onMenuLinkClick={this.toggleSidebar} />

                    <NotificationToastList />

                    <Sidebar
                        closeSidebar={this.closeSidebar}
                        collapseSidebar={this.collapseSidebar}
                        sidebarCollapsed={this.appStore.storage.sidebarCollapsed && !this.appStore.isMedium}
                        sidebarVisible={this.sidebarVisible}
                        match={this.props.match}
                        history={this.props.history}
                        location={this.props.location}
                        staticContext={this.props.staticContext}
                    />

                    <SidebarObfuscator
                        onClick={this.closeSidebar}
                        active={this.sidebarVisible && this.appStore.isMedium}
                    />

                    <MainContainer mainCointainerRef={this.setMainContainer}>
                        <span>
                            <Alert alertHandle={this.appStore.alertHandle} />
                        </span>

                        <span>
                            <NotificationAlert notificationHandle={this.appStore.notificationHandle} />
                        </span>
                        
                        <ContactExpirationAlertsModal modalHandle={this.contractAlertModalHandle}></ContactExpirationAlertsModal>

                        <ImpersonatingAlert
                            isImpersonating={this.accountStore.user?.isImpersonating ?? false}
                            onClick={this.accountStore.signoutRedirect}
                        />

                        <Switch>
                            <PrivateRoute
                                path="/"
                                exact
                                component={Dashboard}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/bi"
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                                component={BIView}
                            />

                            <PrivateRoute
                                path="/dashboard"
                                exact
                                component={Dashboard}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/displays"
                                exact
                                component={DisplayList}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/alert-rules"
                                component={DisplayAlertsView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/displays/:serialNumber"
                                component={DisplayView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/warranty-alerts"
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser', 'PortalLiteUser']}
                                component={WarrantyAlertsView}
                            />

                            <PrivateRoute
                                path="/favorites"
                                exact
                                component={FavoritesList}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/preferences"
                                component={Preferences}
                                user={this.accountStore.user}
                                roles={[
                                    'MriAdmin',
                                    'PortalAdmin',
                                    'MriUser',
                                    'PortalUser',
                                    'ProdTestUser',
                                    'ProdUser',
                                    'PortalLiteUser',
                                ]}
                            />

                            <PrivateRoute
                                path="/accounts"
                                component={AccountsView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/support/documents"
                                component={DocumentsView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser']}
                            />

                            <PrivateRoute
                                path="/users"
                                component={UsersView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin']}
                            />

                            <PrivateRoute
                                path="/invites"
                                component={InvitesList}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin']}
                            />

                            <PrivateRoute
                                path="/support/tickets"
                                exact
                                component={TicketsView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin']}
                            />
                            <PrivateRoute
                                path="/support/contact"
                                exact
                                component={ContactUsView}
                                user={this.accountStore.user}
                                roles={['MriAdmin', 'PortalAdmin', 'MriUser', 'PortalUser', 'PortalLiteUser']}
                            />
                            <PrivateRoute
                                path="/admin"
                                exact
                                component={AdminView}
                                user={this.accountStore.user}
                                roles={['MriAdmin']}
                            />
                            <Route component={NotFound} />
                        </Switch>
                    </MainContainer>

                    <Admin2FAModal
                        location={this.props.location}
                        url="/preferences"
                        requires2FA={
                            !this.accountStore.user?.twoFactorEnabled &&
                            !this.accountStore.isTestUser &&
                            this.accountStore.isInRole('MriAdmin')
                        }
                    />
                    <LoadGoogleMapScript googleMapApiKey={googleMapApiKey} />
                </div>
            </ErrorView>
        );
    }
    
    // Need this function to initialize failing accountStorage props.
    @bind
    @action
    private initializeAccountStorage(){
        if(!this.accountStore.storage.expiringAndExpiredContracts) this.accountStore.storage.expiringAndExpiredContracts = [];
    }
    
    @bind
    @action
    private setMainContainer(r: HTMLDivElement | null) {
        this.appStore.mainContainer = r;
    }

    @bind
    @action
    private async toggleSidebar(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.sidebarVisible = !this.sidebarVisible;
    }

    @bind
    @action
    private closeSidebar(event: React.MouseEvent<LinkContainer | HTMLAnchorElement | HTMLDivElement>) {
        this.sidebarVisible = false;
    }

    @bind
    @action
    private collapseSidebar() {
        this.appStore.storage.sidebarCollapsed = !this.appStore.storage.sidebarCollapsed;
    }

    @bind
    private onHistoryChange(location: Location, trigger: Action): any {
        this.appStore.showHeader();

        this.reportPageView(location);
    }

    private async reportPageView(location: Location) {
        try {
            const match = /^\/displays\/(?<displaySerialNumber>[A-Z0-9]+)/.exec(location.pathname);
            const displaySerialNumber = match && match.groups ? match.groups.displaySerialNumber : null;

            await this.auditStore.logPageView(location.pathname, displaySerialNumber);
        } catch (e: any) {
            console.error(e, 'Failed to logPageView');
        }
    }

    @computed
    private get layoutClassName() {
        return `layout-container ${
            !this.appStore.storage.sidebarCollapsed ? 'layout-sidebar' : 'layout-sidebar-collapsed'
        }`;
    }
    
    @computed
    private get contracts(): Contract[]{
        return toArray(this.contractsStore.contracts);
    }

    @computed
    private get shouldOpenContractsExpirationModal() {
        const isAdmin = this.accountStore.isInRoles(['MriAdmin']);
        if (isAdmin) return false;
        
        const delayDate = this.accountStore.storage.contractExpirationModalDelayDate ? new Date(this.accountStore.storage.contractExpirationModalDelayDate) : undefined;
        const now = new Date();
        const enforceDelay = delayDate && delayDate > now;
        
        if (this.contracts) {
            for (const contract of this.contracts) {
                const contractID = contract.serviceCO || contract.contractNumber;
                const alreadyInExpiringContracts = contractID ? this.accountStore.storage.expiringAndExpiredContracts.includes(contractID) : false;
                
                if (contract.expiringWithin90Days() || contract.expiredRecently()) {
                    if(!enforceDelay || !alreadyInExpiringContracts) {
                        return true;
                    }
                }
            }
        }

        return false;
    }
    
    @observable
    private sidebarVisible = false;
    @observable
    private contractAlertModalHandle= new ModalHandle();
    @observable
    private loading = true;
    @inject
    private errorStore!: ErrorStore;
    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private auditStore!: AuditStore;
}
